/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import moment from 'moment';
import { combineEpics } from 'redux-observable';
import { concat, empty, from, interval, merge, of, timer } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, concatMap, debounceTime, delay, distinctUntilChanged, exhaustMap, expand, filter, finalize, map, mapTo, pluck, reduce, switchMap, tap, mergeMap, withLatestFrom, ignoreElements } from 'rxjs/operators';
import { ActionType, isActionOf } from 'typesafe-actions';
import {AppAction, fetchServerGeneratedPatchesActions, formUpdateReducer} from '../..';
import config from '../../../config';
import { DairyFARMDB, getDB, storeNames, withRetry_ } from '../../../db/store';
import { OFFLINE_IMAGE_UPDATE_STORE } from '../../../db/stores/images';
import { PatchSchema, QueuedPatchSchema } from '../../../db/stores/patches';
import { Evaluation } from '../../../evaluation';
import { createEpic, getCommonHeaders } from '../../../utils/epics';
import { TEN_MINUTES } from '../../../utils/time';
import { CONTENT_TYPE_HEADER, LAST_MODIFIED, PagedResponse } from '../../../utils/types';
import { allDataUploaded, applyRemotePatches, checkAllDataUploaded, clearEvaluation, createRemoteEval, deleteEvaluation, evaluationSaved, fetchAllEvalPatchesActions, fetchEvalPatchesActions, fetchEvaluation, loadEvaluationCompleted, loadEvaluationsCompleted, patchQueued, purgePatchLogs, pushPatchActions, saveEval, scanUncreatedEvals, setActiveEval, syncEvaluations, syncingEvals, loadEvaluations, failedPatchQueued } from '../actions';
import { PatchLog } from './../../../db/stores/patches';
import { EvaluationUpdateAction, FormUpdatePayload, loadFormsActions, updateForm, UPDATE_EVALUATION, uploadImageActions } from './../../forms/actions';
import { signOutAction } from './../../session/actions';
import { addedRemoteIdentifier } from './../actions';
import { newEvalId } from './storageEpics';
import { IDBPCursorWithValue, IDBPDatabase, IDBPObjectStore } from 'idb';
import { isAdmin, isEvaluatorOfEval } from '../../../utils/user';
import { EvalListFilters } from '../../../react/components/EvalList';

const { EVALS_STORE, EVALS_TO_CREATE_STORE, LIVE_EVAL_PATCHES_STORE, PENDING_EVAL_PATCHES_STORE } = storeNames;
const BACKGROUND_SYNC_INTERVAL = TEN_MINUTES; // 10 minutes
export const LAST_EVALS_CHECK_KEY = 'LAST_EVALS_CHECK';

/**
 * Old evaluation types that are not supported by the eval app.
 * The app should not try to fetch any definitions or patches of these types.
 */
const BLACKLISTED_EVAL_TYPES = [
  'farm_ac',
];

const scanUncreatedEvalsEpic = createEpic((action$) => {
  return merge(interval(BACKGROUND_SYNC_INTERVAL), action$.pipe(filter(isActionOf(scanUncreatedEvals)))).pipe(
    exhaustMap(() =>
      from(
        withRetry_<IDBPCursorWithValue<IDBDatabase, [never], never, unknown> | null>((db: IDBPDatabase<DairyFARMDB>) => {
            return db
              .transaction([EVALS_TO_CREATE_STORE])
              .objectStore(EVALS_TO_CREATE_STORE)
              .openCursor()
          })
      ).pipe(
        expand((cursor) => (cursor === null ? empty() : from(cursor.continue()))),
        switchMap((cursor) => (cursor !== null ? of(createRemoteEval.request(cursor!.value)) : empty()))
      )
    )
  );
});

const syncCreatedEvalEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(createRemoteEval.request)),
    pluck('payload'),
    concatMap((payload) => {
      return ajax
        .post(
          `${config.dfdmHost}/api/evaluations/${payload.formId}`,
          {
            id: payload.id,
            author: payload.author,
            definition_id: payload.formUuid,
          },
          getCommonHeaders(state$)
        )
        .pipe(
          map((res) => res.response),
          switchMap(
            (created: {
              id: string;
              definitionType: string;
              definitionMajorVersion: number;
              create_date: string;
              type: string;
            }) => {
              return from(
                withRetry_<Evaluation>(function(db: IDBPDatabase<DairyFARMDB>) {
                  const tx = db.transaction([EVALS_STORE, EVALS_TO_CREATE_STORE], 'readwrite');
                  const evalStore = tx.objectStore(EVALS_STORE);
                  return (evalStore.get(payload.id) as Promise<Evaluation>).then(function attachUUID(savedEval) {
                    const newEvalObj: Evaluation = {
                      ...savedEval!,
                      uuid: created.id,
                      created: created.create_date,
                      modified: created.create_date,
                      lastSync: created.create_date
                    };

                    return evalStore
                      .put(newEvalObj)
                      .then(() => tx.objectStore(EVALS_TO_CREATE_STORE).delete(payload.id))
                      .then(() => newEvalObj);
                  });
                })
              ).pipe(
                map((updatedEval) => createRemoteEval.success(updatedEval)),
                catchError((e) => of(createRemoteEval.failure(e)))
              );
            }
          ),
          catchError((e) => {
            if (e.response) {
              const res = e.response as AjaxResponse;
              switch (res.status) {
                case 401:
                  return of(signOutAction());
                case 403:
                  // this may be causing the eval to not exist. wouldn't users have seen this alert though?
                  alert('Error: You are not allowed to create this kind of evaluation');
                  return from([createRemoteEval.failure(e), clearEvaluation.request(payload.id)]);
                case 404:
                case 405:
                  console.log('Server appears to be down');
                  break;
                case 500:
                  console.log('Server problem', res);
                  break;

                default:
                  break;
              }
            } else {
              console.log('Unrecognized error', e);
            }
            return of(createRemoteEval.failure(e));
          })
        );
    }),
    catchError((e) => of(createRemoteEval.failure(e)))
  );
});

const scheduleUncreatedEvalsBGScans = createEpic((action$) => {
  return merge(
    interval(BACKGROUND_SYNC_INTERVAL).pipe(mapTo(scanUncreatedEvals())),
    action$.pipe(
      filter(isActionOf(scanUncreatedEvals)),
      switchMap((action) =>
        of(action).pipe(
          // reschedule as soon as it happens
          delay(1000 * 60 * 15)
        )
      )
    )
  );
});

const updatePatchUUIDEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(createRemoteEval.success)),
    pluck('payload'),
    switchMap((payload) =>
      from(
        withRetry_((db: IDBPDatabase<DairyFARMDB>) => {
          const store = db.transaction([LIVE_EVAL_PATCHES_STORE], 'readwrite').objectStore(LIVE_EVAL_PATCHES_STORE);

          return store.get(payload.id).then((existingPatch) => {

            if (!existingPatch) return Promise.resolve(null);

            return store.put({ ...existingPatch, uuid: payload.uuid! });
          });
        })
      )
      .pipe(map(() => addedRemoteIdentifier(payload.id)))
    )
  );
});

/**
 * Look for all evaluations that have failed to sync that also have un-synced patches in the live eval patch store
 */
const queueFailedPatchesEpic = createEpic((action$, state$) => {
  return merge(
    timer(1000, BACKGROUND_SYNC_INTERVAL),
    action$.pipe(filter(isActionOf(patchQueued)), debounceTime(10000)),
  )
  .pipe(
    exhaustMap(() => {
      const failedEvaluations = state$.value.evals.items.filter(evaluation => evaluation.syncStatus === 'FAILED');

      // when a live patch is updated, set it to 'synced: false'
      return from(
        withRetry_<PatchSchema[]>((db: IDBPDatabase<DairyFARMDB>) => {
          return db.getAll(LIVE_EVAL_PATCHES_STORE);
        }))
          .pipe(
            map((patches) => {
              return patches
                .filter(patch => {
                  return !patch.synced && failedEvaluations.keySeq().toArray().includes(patch.evalId);
                })
            }),
            switchMap((evalIDs) => from(evalIDs)),
            map(patch => failedPatchQueued(patch.evalId))
          );
    })
  )
})

/**
 * Transfer a single patch from the PENDING store the the LIVE store
 */
export function transferPatch(liveCursor: IDBPCursorWithValue | null, pendingStore: IDBPObjectStore<DairyFARMDB>, allowMergeDuringLock = false): Promise<void> {
  if (liveCursor === null) {
    return Promise.resolve();
  }

  const patch = liveCursor.value;
  // only queue patches that have a uuid they can PATCH to
  if (patch.uuid === null) {
    return liveCursor.continue().then(liveCursor => transferPatch(liveCursor, pendingStore));
  }
  return pendingStore
    .get(patch.evalId)
    .then(function (existingPatch: any) {
      if (!existingPatch || allowMergeDuringLock || !existingPatch.locked) {
        return Promise.resolve(existingPatch)
          .then(function consolidatePatches(existingPatch) {
            if (!existingPatch) {
              return patch;
            }

            return {
              evalId: patch.evalId,
              patches: [...existingPatch.patches, ...patch.patches],
              uuid: patch.uuid,
            };
          })
          .then((patch) => pendingStore.put(patch as QueuedPatchSchema))
          .then(() => liveCursor.delete())
          .then(() => liveCursor.continue())
          .then(liveCursor => transferPatch(liveCursor, pendingStore));
      } else {
        return Promise.resolve()
          .then(() => liveCursor.continue())
          .then(liveCursor => transferPatch(liveCursor, pendingStore));
      }
    });
}

/**
 * Move patches from live store to pending store, lock patch in pending store.
 * Once this patch is in the pending patch store, no mor emutations can be applied to it.
 * A backlog of patches will accumulate in the live patch store until the pending patch is successfully synced.
 */
const queuePatchesEpic = createEpic((action$) => {
  return merge(
    timer(1000, BACKGROUND_SYNC_INTERVAL),
    action$.pipe(filter(isActionOf(patchQueued)), debounceTime(10000)), // ten seconds after edits
    action$.pipe(
      filter(isActionOf([addedRemoteIdentifier, fetchEvalPatchesActions.success, fetchAllEvalPatchesActions.success]))
    )
  ).pipe(
      exhaustMap(function transferLivePatches() {
      return from(
        withRetry_(function (db: IDBPDatabase<DairyFARMDB>) {
          const tx = db.transaction([LIVE_EVAL_PATCHES_STORE, PENDING_EVAL_PATCHES_STORE], 'readwrite');
          const liveStore = tx.objectStore(LIVE_EVAL_PATCHES_STORE);
          const pendingStore = tx.objectStore(PENDING_EVAL_PATCHES_STORE);

          return liveStore.openCursor().then((liveCursor) => transferPatch(liveCursor as any, pendingStore as any));
        })
      )
    }),
    exhaustMap(function attemptSync() {
      return from(
        withRetry_<string[]>((db: IDBPDatabase<DairyFARMDB>) => {
          return db.getAllKeys(PENDING_EVAL_PATCHES_STORE);
        }))
        .pipe(
        switchMap((evalIDs) => from(evalIDs)),
        // FIXME: This presumes only the author uses the device. Should only upload patches for current user
        map((evalID) => pushPatchActions.request(evalID))
      );
    }),
    catchError((e) => {
      console.error('failed to push patches:', e);
      return of(pushPatchActions.failure({ id: '', error: e }));
    })
  );
});

// todo whenever network is detected, instead of waiting by chance
const pushPatchLogEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(pushPatchActions.request)),
    pluck('payload'),
    concatMap((id) => {
      // get eval type
      return from(
        withRetry_<{ formType: string, id: string, uuid: string, patch: any, evl: Evaluation } | null>(function(db: IDBPDatabase<DairyFARMDB>) {
          const tx = db.transaction([EVALS_STORE, PENDING_EVAL_PATCHES_STORE], 'readwrite');
          const patchStore = tx.objectStore(PENDING_EVAL_PATCHES_STORE);

          return patchStore.get(id).then((patch) => {
            if (!patch) {
              return null;
            }

            // this gets it from eval store (indexed db), not from the store (in-memory)
            // lock the pending patch, send it, and if it returns, update the eval and delete the patch
            // on error should we just unlock the patch?
            return (tx.objectStore(EVALS_STORE).get(id) as Promise<Evaluation>).then((evl) => {
              if(!isEvaluatorOfEval(evl, state$.value) && !isAdmin(state$.value)) {
                return null;
              }
              return patchStore
                .put({ ...patch!, locked: true })
                .then(() => {
                  return {formType: evl.formType, id: evl.id, uuid: evl.uuid, patch, evl}
                })
            });
          });
        })
      ).pipe(
        switchMap((result) => (result ? of(result) : empty())),
        switchMap(({ formType, id, uuid, patch, evl }) => {
          const headers: {[key: string]: string} = {
            ...getCommonHeaders(state$),
            [CONTENT_TYPE_HEADER]: 'application/json',
          }

          if (evl.lastSync) {
            headers['If-Unmodified-Since'] = evl.lastSync!;
          }

          return from(
            fetch(`${config.dfdmHost}/api/evaluations/${formType}/${uuid}`, {
              method: 'PATCH',
              headers,
              body: JSON.stringify(patch!.patches),
            })
              .then(function handleResponse(
                response
              ): Promise<
                | ActionType<typeof pushPatchActions.success>
                | ActionType<typeof fetchEvalPatchesActions.request>
                | ActionType<typeof pushPatchActions.failure>
                | ActionType<typeof signOutAction>
              > {
                switch (response.status) {
                    case 200:
                    return response.json().then((evalData) => {
                      return withRetry_<Evaluation>(function(db: IDBPDatabase<DairyFARMDB>) {
                        const tx = db.transaction([EVALS_STORE, PENDING_EVAL_PATCHES_STORE], 'readwrite');
                        const evlStore = tx.objectStore(EVALS_STORE);
                        // todo need to actually persist it since it isn't in redux-persist.
                        return evlStore
                          .get(id)
                          .then((evl) => ({
                            ...evl!,
                            evaluatorId: evalData.evaluatorId,
                            evaluator2Id: evalData.evaluator2Id,
                            data: evalData,
                            lastSync: moment(response.headers.get(LAST_MODIFIED)!).toISOString(),
                          }))
                          .then((updatedEvl) =>
                            evlStore
                              .put(updatedEvl)
                              .then(() => tx.objectStore(PENDING_EVAL_PATCHES_STORE).delete(id))
                              .then(() => updatedEvl)
                          );
                      })
                      .then((data) => {
                          return pushPatchActions.success({id, lastSync: data.lastSync, uuid: data.uuid, since: evalData.refetch});
                      })
                    });
                  case 401:
                    return Promise.resolve(signOutAction());
                  case 412:
                    console.log('GOT A 412, REFETCHING PATCHES FOR THIS EVALUATION!:', new Date().getTime());
                    return Promise.resolve(fetchEvalPatchesActions.request(uuid));
                  default:
                    return response.json().then((err) => {
                      return pushPatchActions.failure({id: id!, error: new Error(JSON.stringify(err)), httpStatus: err.code});
                    });
                }
              })
              .catch((e) => {
                return pushPatchActions.failure({id: id!, error: e});
              })
          );
        })
      );
    })
  );
});

const scheduleEvalSync = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf([syncEvaluations, loadFormsActions.success])),
    filter((_) => {
      // Don't sync as often. Note if the user refreshes this will postpone eval sync another 5 minutes from the timestamp
      const lastCheck = localStorage.getItem(LAST_EVALS_CHECK_KEY);
      return (
        !lastCheck ||
        moment(lastCheck)
          .add(BACKGROUND_SYNC_INTERVAL, 'ms')
          .isBefore(moment())
      );
    }),

    switchMap(() => {
      // this shouldn't include farm_ac, should it?
      const formTypes: string[] = Object
        .entries(state$.value.session.dfdmToken!.evaluations)
        .filter(entry => !BLACKLISTED_EVAL_TYPES.includes(entry[0]))
      // TODO: this could be expanded to include 'testing' to test new dev forms
        .map(([type]) => type);
      if (formTypes.length === 0) return empty();
      return from(formTypes).pipe(
        // Don't sync for another 5 minutes. Note that this doesn't write after the sync, just after the sync is queued.
        //  As such there are implications for refreshing the page prior to eval sync completion
        finalize(() => localStorage.setItem(LAST_EVALS_CHECK_KEY, moment().toISOString()))
      );
    }),
    map((type) => fetchAllEvalPatchesActions.request(type))
  );
});

type EvalLog = {
  eval_id: string;
  edit_date: string;
  published: boolean;
  uri: string;
};
type AllEvalPatchesLogResponse = PagedResponse<EvalLog>;

// check for all eval updates
const pollEvalUpdatesEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchAllEvalPatchesActions.request)),
    pluck('payload'),
    concatMap((type, payload) => {
      const headers = getCommonHeaders(state$);
      // Fetch updates from each kind of evaluation
      /* 
          FIXME: Determine how to narrow our search to only
          those that have updated since our last check, or
          those we do not have locally
          */
      return concat(
        ajax.get(`${config.dfdmHost}/api/evaluations/${type}/patches`, headers).pipe(
          expand((res) => {
            const response = res.response as AllEvalPatchesLogResponse;
            if (!response || !response.next) return empty();

            return ajax.get(response.next, headers);
          }),
          map((res) => res.response as AllEvalPatchesLogResponse),
          concatMap((res) => from(res.results)),
          concatMap(({ eval_id, edit_date, published }) => {
            const maxAge = config.versionMapping[type].maxAge;
            
            let shouldIgnore = false;
            let limit: moment.Moment;

            if (maxAge !== -1) {
              limit = moment()
                .startOf('day')
                .subtract(maxAge, 'days');
              shouldIgnore = published && limit.isAfter(edit_date);
            }
              
            /**
             if eval is local
              then fetch patches
              else fetch evaluation
              */
            return from(
              withRetry_((db: IDBPDatabase<DairyFARMDB>) => {
                return db.getFromIndex(EVALS_STORE, 'uuid', eval_id);
              })
              .then((evl: any) => {
                // A wrong form type, farm_ac_evaluator, was returned by the backend instead of farm_ac_v5 when synching between devices
                // This corrects the form type for these evaluations
                if (evl && evl.formType === 'farm_ac_evaluator') {
                  withRetry_((db: IDBPDatabase<DairyFARMDB>) => {
                    return db.put(EVALS_STORE, {...evl, formType: type});
                  })
                }
                
                if (evl) {
                  if (shouldIgnore && limit?.isAfter(evl.modified)) {
                    // get rid of evaluations published longer than 90 days ago
                    return deleteEvaluation.request({ id: evl.id, type: evl.formType });
                  }

                  // Only fetch patches if there are new changes
                  // TODO: Use moments in the state object and just serialize them out on persist
                  return moment(edit_date).isAfter(evl.lastSync!) ? fetchEvalPatchesActions.request(eval_id) : null;
                }

                // this fetches duplicates for some reason. i have 4 freeman bontragers in my app
                return shouldIgnore
                  ? null
                  : fetchEvaluation.request({
                      uuid: eval_id,
                      formType: type,
                    });
              })
            ).pipe(switchMap((action) => (action === null ? empty() : of(action))));
          })
        ),
        of(fetchAllEvalPatchesActions.success(type))
      ).pipe(
        catchError((e: AjaxError) => {
          const actions: AppAction[] = [fetchAllEvalPatchesActions.failure(e)];
          if (e.status === 401) actions.push(signOutAction());
          return of(...actions);
        })
      );
    })
  );
});

type EvalPatchLog = {
  type: string;
  submitter: string;
  evaluation_id: string;
  submission_date: string;
  published: boolean;
  patch: FormUpdatePayload[];
};
type EvalPatchLogResponse = PagedResponse<EvalPatchLog>;

// get eval updates
const fetchEvalUpdatesEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchEvalPatchesActions.request)),
    pluck('payload'),
    concatMap((uuid) => {
      return from(
        withRetry_<Evaluation>((db: IDBPDatabase<DairyFARMDB>) => {
          return db.getFromIndex(EVALS_STORE, 'uuid', uuid);
        }))
        .pipe(
        filter(evl => {
          return Boolean(evl) && (isEvaluatorOfEval(evl, state$.value) || isAdmin(state$.value));
        }),
        concatMap((evl: Evaluation) => {
          const localId = evl!.id;
          const lastSync = evl!.lastSync!;
          let lastModified: string | undefined = undefined;
          // this action being fired mandates that the evaluation exists already in the db
          return ajax
            .get(
              `${config.dfdmHost}/api/evaluations/${evl!.formType}/${uuid}/patches?since=${lastSync}&full=true`,
              getCommonHeaders(state$)
            )
            .pipe(
              expand((res) => {
                if (res.response === null) {
                  return empty();
                }

                const response = res.response as EvalPatchLogResponse;

                // See if we have the oldest changes in this log
                const oldestPatch = response.results[response.results.length - 1];
                return (!oldestPatch || (evl && evl.lastSync !== oldestPatch.submission_date)) && response.next
                  ? ajax.get(response.next, getCommonHeaders(state$))
                  : empty();
              }),
              concatMap((res) => {
                const serverTime = res.xhr.getResponseHeader('Last-Modified')!;
                lastModified = new Date(serverTime!).toISOString();

                return from((res.response as EvalPatchLogResponse).results);
              }),
              filter((log) => {
                return new Date(log.submission_date) > new Date(lastSync);
              }),
              reduce((acc, curr) => {
                return {
                  lastModified: acc.lastModified || curr.submission_date,
                  patches: [...acc.patches, ...curr.patch],
                }
              }, { patches: [] } as { lastModified?: string; patches: PatchLog }),
              switchMap((remotePatchSummary) => {
                const remotePatches = remotePatchSummary.patches;
                if (remotePatches.length === 0) {
                  return from(
                    withRetry_<PatchLog>(function aggregatePatches(db: IDBPDatabase<DairyFARMDB>) {
                      const tx = db.transaction(EVALS_STORE, 'readwrite');
                      const evalStore = tx.objectStore(EVALS_STORE);

                      return (tx.objectStore(EVALS_STORE).get(localId) as Promise<Evaluation>)
                      .then((evl) => {
                        return evalStore
                          .put({ ...evl!, lastSync: lastModified! ?? evl.lastSync })
                      })
                  
                    })
                  )
                  .pipe(
                    map(_ => {
                      return fetchEvalPatchesActions.success({ id: localId, lastSync: lastModified! });
                    })
                  );
                }
                  return from(
                    withRetry_<PatchLog>(function aggregatePatches(db: IDBPDatabase<DairyFARMDB>) {
                      // Aggregate pending patches and live patches
                      const tx = db.transaction([PENDING_EVAL_PATCHES_STORE, LIVE_EVAL_PATCHES_STORE], 'readwrite');
                  
                      // return liveStore.openCursor()
                      //   .then((liveCursor) => transferPatch(liveCursor as any, pendingStore as any))
                      //   .then(_ => {
                      //     return pendingStore.get(localId)
                      //   })
                      //   .then((pendingPatches) => {
                      //     if (pendingPatches) {
                      //       return pendingPatches.patches;
                      //     }
                      //     return []
                      //   });

                      return tx
                        .objectStore(LIVE_EVAL_PATCHES_STORE)
                        .get(localId)
                        // .getAll()
                        .then((livePatches) => {
                          return tx
                            .objectStore(PENDING_EVAL_PATCHES_STORE)
                            .get(localId)
                            .then((pendingPatches) => {

                              let patches: PatchLog = [];
                              // only consider PENDING patches, as this is what will be sent to the server
                              if (pendingPatches) {
                                patches = patches.concat(pendingPatches.patches);
                              }
                              if (livePatches) {
                                patches = patches.concat(livePatches.patches);
                              }
                              return patches;
                            })
                        });
                    })
                  )
                .pipe(
                  switchMap(function checkForConflicts(localPatches) {
                    const finalRemoteState: {[key: string]: any} = {};
                    const finalLocalState: {[key: string]: any} = {};
                    remotePatches.sort((a, b) => {
                      return new Date(a.patchedAt).getTime() - new Date(b.patchedAt).getTime();
                    }).forEach(patch => {
                      if (patch.op === 'add' || patch.op === 'replace') {
                        finalRemoteState[patch.path] = patch.value;
                      } else if (patch.op === 'remove') {
                        finalRemoteState[patch.path] = null;
                      }
                    })
                    localPatches.forEach(patch => {
                      if (patch.op === 'add' || patch.op === 'replace') {
                        finalLocalState[patch.path] = patch.value;
                      } else if (patch.op === 'remove') {
                        finalLocalState[patch.path] = null;
                      }
                    })

                    const conflicts: {path: string, remoteValue: any, localValue: any}[] = [];
                    Object.entries(finalLocalState).forEach(([key, value]) => {
                      if (finalRemoteState.hasOwnProperty(key)) {
                        if (finalRemoteState[key] !== value) {
                          conflicts.push({
                            path: key,
                            localValue: value,
                            remoteValue: finalRemoteState[key]
                          })
                        }
                      }
                    });

                    // instead of aborting, loop over patches by timestamp. Take the later one
                    const pathsWrittenLocally = new Set(localPatches.map((patch) => patch.path));
                    const firstConflict = remotePatches.findIndex((remotePatch) => {
                      return pathsWrittenLocally.has(remotePatch.path)
                    });

                    const allConflicts = remotePatches.filter((remotePatch) => {
                      return pathsWrittenLocally.has(remotePatch.path)
                    });

                    if (conflicts.length === 0) {
                      // no conflicts, apply all remote patches to current form
                      const updateActions: EvaluationUpdateAction[] = remotePatches.map((patch) =>
                        updateForm({ evalID: localId, payload: patch })
                      );

                      // Compute the new evaluation state
                      let evaluation = evl!.data;
                      updateActions.forEach((action) => {
                        evaluation = formUpdateReducer(evaluation, action);
                      });

                      const lastSync = moment().toISOString();

                      return from([
                        applyRemotePatches(updateActions),
                        saveEval({
                          evalId: localId,
                          eval: {
                            ...evl!,
                            data: evaluation,
                            lastSync,
                          },
                        }),
                        fetchEvalPatchesActions.success({ id: localId, lastSync }),
                      ]);
                    }

                    alert(
                      "There are conflicting changes from another device. Your changes have overwritten the following answers:" +
                      `${conflicts.map(c => {
                        return '\n Question: ' + c.path + '\n Value from other device: ' + JSON.stringify(c.remoteValue) + '\n Value from your device: ' + JSON.stringify(c.localValue)
                      })}`
                    );

                    // only apply remote patches that do not conflict with what is on-device
                    const updateActions: EvaluationUpdateAction[] = remotePatches.map((patch) => {
                      const conflict = conflicts.find(c => {
                        return c.path === patch.path;
                      })
                      if (!conflict) {
                        return updateForm({ evalID: localId, payload: patch })
                      }
                      return undefined;
                    })
                    .filter(c => c) as EvaluationUpdateAction[];

                    // Compute the new evaluation state
                    let evaluation = evl!.data;
                    updateActions.forEach((action) => {
                      evaluation = formUpdateReducer(evaluation, action);
                    });

                    const lastSync = moment().toISOString();
                    
                    if (updateActions.length > 0) {

                      return from([
                        applyRemotePatches(updateActions),
                        saveEval({
                          evalId: localId,
                          eval: {
                            ...evl!,
                            data: evaluation,
                            lastSync,
                          },
                        }),
                        fetchEvalPatchesActions.success({ id: localId, lastSync }),
                        fetchEvaluation.request({
                          uuid,
                          localId,
                          formType: evl!.formType,
                        }),
                      ]);
                    } else {
                      return from([
                        saveEval({
                          evalId: localId,
                          eval: {
                            ...evl!,
                            data: evaluation,
                            lastSync,
                          },
                        }),
                        fetchEvalPatchesActions.success({ id: localId, lastSync }),
                        fetchEvaluation.request({
                          uuid,
                          localId,
                          formType: evl!.formType,
                        }),
                      ]);
                    }


                    // if conflict, purge local patches after conflict, apply remote patches and then local patches

                    // return from([
                    //   purgePatchLogs.request({
                    //     evalId: localId,
                    //     fromPatchToPath: remotePatches[firstConflict].path,
                    //   }),
                    //   fetchEvalPatchesActions.success({ id: localId, lastSync: evl!.lastSync! }),
                    //   fetchEvaluation.request({
                    //     uuid,
                    //     localId,
                    //     formType: evl!.formType,
                    //   }),
                    // ]);
                  })
                );
              }),
              catchError((e: AjaxError) => {
                const actions: AppAction[] = [fetchEvalPatchesActions.failure(e)];
                if (e.status === 401) actions.push(signOutAction());
                return of(...actions);
              })
            );
        })
      )
    })
  )
});

const fetchServerGeneratedPatches = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchServerGeneratedPatchesActions.request)),
    pluck('payload'),
    concatMap((payload) => {
      const uuid = payload.id;

      return from(getDB().then((db) => db.getFromIndex(EVALS_STORE, 'uuid', uuid))).pipe(
        filter(evl => Boolean(evl)),
        concatMap((evl) => {
          const localId = evl!.id;
          const lastSync = payload.since ?? evl!.lastSync!;
          // this action being fired mandates that the evaluation exists already in the db
          const params = `full=true`;
          return ajax
            .get(
              `${config.dfdmHost}/api/evaluations/${evl!.formType}/${uuid}/patches?${params}`,
              getCommonHeaders(state$)
            )
            .pipe(
              map((res) => {
                if (res.response === null) {
                  return empty();
                }

                const response = res.response as EvalPatchLogResponse;
                // See if we have the oldest changes in this log
                const oldestPatch = response.results[0];

                return oldestPatch;
              }),
              switchMap((remotePatchSummary: any) => {
                // this spot needs logging
                const remotePatches = remotePatchSummary.patch as any;
                if (remotePatches.length === 0) {
                  // this spot needs logging
                  return of(fetchEvalPatchesActions.success({ id: localId, lastSync: evl!.lastSync! }));
                }
                // this spot needs logging
                return from(
                  withRetry_<PatchLog>(function aggregatePatches(db: IDBPDatabase<DairyFARMDB>) {
                    // Aggregate pending patches and live patches
                    const tx = db.transaction([PENDING_EVAL_PATCHES_STORE, LIVE_EVAL_PATCHES_STORE]);
                    return tx
                      .objectStore(PENDING_EVAL_PATCHES_STORE)
                      .get(localId)
                      .then((pendingPatches) =>
                        tx
                          .objectStore(LIVE_EVAL_PATCHES_STORE)
                          .get(localId)
                          .then((livePatches) => {
                            const patches: PatchLog = [];
                            if (pendingPatches) {
                              patches.concat(pendingPatches.patches);
                            }
                            if (livePatches) {
                              patches.concat(livePatches.patches);
                            }
                            return patches;
                          })
                      );
                  })
                ).pipe(
                  switchMap(function checkForConflicts(localPatches) {
                    // this spot needs logging
                    const pathsWrittenLocally = new Set(localPatches.map((patch) => patch.path));
                    const firstConflict = remotePatches.findIndex((remotePatch: any) =>
                      pathsWrittenLocally.has(remotePatch.path)
                    );

                    // this spot needs logging
                    if (firstConflict < 0) {
                      // no conflicts, apply all remote patches to current form
                      const updateActions: EvaluationUpdateAction[] = remotePatches.map((patch: any) =>
                        updateForm({ evalID: localId, payload: patch })
                      );

                      // Compute the new evaluation state
                      let evaluation = evl!.data;

                      updateActions.forEach((action) => {
                        evaluation = formUpdateReducer(evaluation, action);
                      });

                      const lastSync = moment(remotePatchSummary.lastModified).toISOString();
                      // const lastSync = remotePatchSummary.lastModified!;
                      // Apply all patches, then also persist the latest remote eval state
                      return from([
                        applyRemotePatches(updateActions),
                        saveEval({
                          evalId: localId,
                          eval: {
                            ...evl!,
                            data: evaluation,
                            lastSync,
                          },
                        }),
                        fetchEvalPatchesActions.success({ id: localId, lastSync }),
                      ]);
                    }

                    // this needs to be fixed. maybe a patch with an id can be returned to the client
                    // alert(
                    //   "Oops! We couldn't entirely sync an evaluation because there's overlapping changes from another device. We saved the cloud version and tried re-applying your changes prior to the overlap. Please double-check the evaluation for correctness."
                    // );

                    // if conflict, purge local patches after conflict, apply remote patches and then local patches
                    return from([
                      purgePatchLogs.request({
                        evalId: localId,
                        fromPatchToPath: remotePatches[firstConflict].path,
                      }),
                      fetchEvalPatchesActions.success({ id: localId, lastSync: evl!.lastSync! }),
                      fetchEvaluation.request({
                        uuid,
                        localId,
                        formType: evl!.formType,
                      }),
                    ]);
                  })
                );
              }),
              catchError((e: AjaxError) => {
                const actions: AppAction[] = [fetchEvalPatchesActions.failure(e)];
                if (e.status === 401) actions.push(signOutAction());
                return of(...actions);
              })
            );
        })
      )
    })
  );
});

const fetchEvaluationEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchEvaluation.request)),
    pluck('payload'),
    mergeMap(({ formType, uuid, localId }) =>{
      return ajax
        .get(`${config.dfdmHost}/api/evaluations/${formType}/${uuid}?patched=true`, {
          ...getCommonHeaders(state$),
        })
        .pipe(
          map((res) => {
            const lastModified = moment(res.xhr.getResponseHeader(LAST_MODIFIED)!).toISOString();

            const evl: Evaluation = res.response;

            const evalId = localId ?? newEvalId();
            return fetchEvaluation.success({
              ...evl,
              id: evalId,
              lastSync: lastModified,
              uuid: evl.id,
            });
          }),
          catchError((e: AjaxError) => {
            const actions: AppAction[] = [fetchEvaluation.failure(e)];
            if (e.status === 401) {
              actions.push(signOutAction());
            } else if (e.status === 404 && localId) {
              actions.push(clearEvaluation.request(localId));
            }
            return of(...actions);
          })
        )
    })
  );
});

const refetchPatchesEpic = createEpic((action$, state$) => {
    return action$.pipe(
        filter(isActionOf(pushPatchActions.success)),
        //consider sending back a timestamp to refetch from, that way the UI will get the patch
        filter(({payload}) => payload.since !== undefined),
        map(({payload}) => {
            return fetchServerGeneratedPatchesActions.request({id: payload.uuid!, since: payload.lastSync});
        })
    )
});

const scheduleSyncStateCheckEpic = createEpic((action$) => {
  return merge(
    action$.pipe(
      filter(isActionOf(loadEvaluationsCompleted)),
      switchMap((action) => from(Object.keys(action.payload)))
    ),
    action$.pipe(
      filter(isActionOf([loadEvaluationCompleted, setActiveEval, pushPatchActions.success])),
      map((action) => action.payload.id)
    ),
    action$.pipe(
      filter(isActionOf([evaluationSaved])),
      map((action) => action.payload)
    ),
    action$.pipe(
      filter(isActionOf(uploadImageActions.success)),
      map((action) => action.payload.evalID)
    )
  ).pipe(map((evalID) => checkAllDataUploaded(evalID)));
});

// TODO: Delete evaluations

const checkSyncStateEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(checkAllDataUploaded)),
    pluck('payload'),
    concatMap((evalID) => {
      if (!state$.value.evals.items.hasIn([evalID, 'lastSync'])) {
        return of({ evalID, allUpdated: false });
      }

      return from(
        withRetry_<boolean[]>((db: IDBPDatabase<DairyFARMDB>) => {
          return Promise.all([
            db.getAllKeysFromIndex(OFFLINE_IMAGE_UPDATE_STORE, 'evalID', evalID, 1).then((a) => a.length === 0),
            db.get(LIVE_EVAL_PATCHES_STORE, evalID).then((a) => a === undefined),
            db.get(PENDING_EVAL_PATCHES_STORE, evalID).then((a) => a === undefined),
          ])
        })
        .then((values) => {
          const empty = values.every((empty) => empty);
          return empty;
        })
        .then((allUpdated) => ({ evalID, allUpdated }))
      )
    }),
    map(({ evalID, allUpdated }) => {
      return allDataUploaded(evalID, allUpdated);
    })
  );
});

const reloadEvaluationsPostDelete = createEpic((action$, state$) => {
  return action$.pipe(
    filter(
      isActionOf(deleteEvaluation.success)
    ),
    withLatestFrom(state$),
    map(([_, state]) => loadEvaluations(state.evals.draftMode, state.evals.limit, state.evals.offset, state.evals.type, state.evals.facilityId, state.evals.searchText, {evalType: state.evals.selectedEvalType, dateRange: state.evals.selectedDateRange}))
  )
})

const syncingWatcher = createEpic((action$) => {
  let activeRequests = 0;
  return merge(
    action$.pipe(
      filter(
        // Track completion of all patch requests
        isActionOf([fetchAllEvalPatchesActions.request, fetchEvaluation.request, fetchEvalPatchesActions.request])
      ),
      mapTo(1)
    ),
    action$.pipe(
      filter(
        // Track completion of all patch requests
        isActionOf([
          fetchAllEvalPatchesActions.success,
          fetchAllEvalPatchesActions.failure,
          fetchEvaluation.success,
          fetchEvaluation.failure,
          fetchEvalPatchesActions.success,
          fetchEvalPatchesActions.failure,
        ])
      ),
      delay(500),
      mapTo(-1)
    )
  ).pipe(
    tap((increment) => (activeRequests += increment)),
    map(() => activeRequests > 0),
    distinctUntilChanged(),
    map(syncingEvals)
  );
});

export default combineEpics(
  scanUncreatedEvalsEpic,
  syncCreatedEvalEpic,
  scheduleUncreatedEvalsBGScans,
  updatePatchUUIDEpic,
  queuePatchesEpic,
  pushPatchLogEpic,
  pollEvalUpdatesEpic,
  scheduleEvalSync,
  fetchEvalUpdatesEpic,
  fetchEvaluationEpic,
  syncingWatcher,
  scheduleSyncStateCheckEpic,
  checkSyncStateEpic,
  reloadEvaluationsPostDelete,
  queueFailedPatchesEpic,
  refetchPatchesEpic,
  fetchServerGeneratedPatches
);